import { setBarcodeErrors, setCAPErrors, setCPIErrors, setPriceErrors, setQuantityErrors, setSKUErrors, setVariationsErrors, setWeightErrors, updateProductErrorsFromVariations, updateProductVariationsErrors } from '@/utils/product/errors';
import { getCombinationsErrors, updateVariationsErrors } from '@/utils/validations/variations';
import { getInventoryFromVariations } from '@/utils/inventory';
import { shapeVariationsForApp } from '@/utils/variations/shapeForApp';
import { isProfileOperation } from '@/utils/variations/operations';
import { reduceErrors } from '@/utils/errors';
import { retain } from '@/utils/variations/retain';
import { get } from '@/utils/iterable/get';

import { EBAY, ETSY, FAIRE, SHOPIFY } from '@/constants/channels';
import { ERROR, OVERSET, VALUE } from '@/constants/product';
import { OPERATIONS } from '@/constants/bulkEdit';
import { ATTRIBUTE } from '@/constants/attributes';
import { EMPTY } from '@/constants';
import { FORM } from '@/constants/forms';
import { DATA } from '@/data';

function updateEbayVariations({ operation, product: source, shop, shopData }) {
  let product = source;
  const channel = product.get('channel');
  const marketplaceId = shop.get('marketplaceId');
  const category = product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_CATEGORY, VALUE]);
  const itemSpecifics = product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS], EMPTY.MAP);

  if (get([EBAY, marketplaceId, 'CATEGORIES', category, 'features', 'variations'])(DATA) === false) return product;

  const asProfile = isProfileOperation({ channel, operation });
  let value = shapeVariationsForApp({ asProfile, data: operation.get(VALUE), product });

  if (asProfile) {
    value = retain({ channel, product, shopData, variations: value, validate: false });
  }

  value = updateVariationsErrors({ category, channel, itemSpecifics, shop, shopData, variations: value });
  product = setVariationsErrors({ product: product.set(FORM.VARIATIONS, value), shop, shopData });

  if (!reduceErrors(product.getIn([FORM.VARIATIONS, ERROR]))) {
    return product;
  }

  return source;
}

function updateEtsyVariations({ operation, product: source, shop, shopData }) {
  let product = source;
  const channel = product.get('channel');

  if (product.getIn([FORM.DETAILS, ATTRIBUTE.DIGITAL, VALUE])) return source;

  const asProfile = isProfileOperation({ channel, operation });
  let value = shapeVariationsForApp({ asProfile, data: operation.get(VALUE), product });

  if (asProfile) {
    value = retain({ channel, product, shopData, variations: value, validate: false });
  }

  value = updateVariationsErrors({ channel, shop, shopData, variations: value });

  if (!reduceErrors(value.get(ERROR))) {
    product = updateProductVariationsErrors({ product: product.set(FORM.VARIATIONS, value), shopData });
  }

  return product;
}

function updateFaireVariations({ operation, product, shop, shopData }) {
  const channel = product.get('channel');
  const asProfile = isProfileOperation({ channel, operation });
  let value = shapeVariationsForApp({ asProfile, data: operation.get(VALUE), product });

  if (asProfile) {
    value = retain({ channel, product, shopData, variations: value, validate: false });
  }

  value = updateVariationsErrors({ channel, shop, shopData, variations: value });

  if (!reduceErrors(value.get(ERROR))) {
    return updateProductVariationsErrors({ product: product.set(FORM.VARIATIONS, value), shopData });
  }

  return product;
}

function updateShopifyVariations({ operation, product: source, shop, shopData }) {
  let product = source;
  const channel = product.get('channel');

  switch (operation.get('type')) {
    case OPERATIONS[SHOPIFY].VARIATIONS: {
      const asProfile = isProfileOperation({ channel, operation });
      let value = shapeVariationsForApp({ asProfile, data: operation.get(VALUE), product });

      if (asProfile) {
        value = retain({ channel, product, shopData, variations: value, validate: false });
      }

      value = updateVariationsErrors({ channel, shop, shopData, variations: value });

      if (!reduceErrors(value.get(ERROR))) {
        product = updateProductVariationsErrors({ product: product.set(FORM.VARIATIONS, value), shopData });
      }

      break;
    }

    case OPERATIONS[SHOPIFY].BARCODE.INLINE_EDIT: {
      let updatedProduct = getInventoryFromVariations({
        channel,
        product,
        variations: operation.get(VALUE),
      });

      const overset = updatedProduct.getIn([FORM.INVENTORY, ATTRIBUTE.BARCODE, OVERSET]);

      if (overset) {
        updatedProduct = updatedProduct.setIn([FORM.VARIATIONS, ERROR],
          getCombinationsErrors({ channel, shopData, variations: updatedProduct.get(FORM.VARIATIONS) })
        );

        updatedProduct = updatedProduct
          .getIn([FORM.VARIATIONS, ERROR])
          .reduce(updateProductErrorsFromVariations, updatedProduct);
      } else {
        updatedProduct = setBarcodeErrors({ product: updatedProduct });
      }

      const error = overset
        ? updatedProduct.getIn([ERROR, FORM.VARIATIONS, ATTRIBUTE.BARCODE])
        : updatedProduct.getIn([ERROR, ATTRIBUTE.BARCODE]);

      if (!error) {
        product = updatedProduct;
      }

      break;
    }

    case OPERATIONS[SHOPIFY].CAP.INLINE_EDIT: {
      let updatedProduct = getInventoryFromVariations({
        channel,
        product,
        variations: operation.get(VALUE),
      });

      const overset = updatedProduct.getIn([FORM.INVENTORY, ATTRIBUTE.CAP, OVERSET]);

      if (overset) {
        updatedProduct = updatedProduct.setIn([FORM.VARIATIONS, ERROR],
          getCombinationsErrors({ channel, shopData, variations: updatedProduct.get(FORM.VARIATIONS) })
        );

        updatedProduct = updatedProduct
          .getIn([FORM.VARIATIONS, ERROR])
          .reduce(updateProductErrorsFromVariations, updatedProduct);
      } else {
        updatedProduct = setCAPErrors({ product: updatedProduct });
      }

      const error = overset
        ? updatedProduct.getIn([ERROR, FORM.VARIATIONS, ATTRIBUTE.CAP])
        : updatedProduct.getIn([ERROR, ATTRIBUTE.CAP]);

      if (!error) {
        product = updatedProduct;
      }

      break;
    }

    case OPERATIONS[SHOPIFY].CPI.INLINE_EDIT: {
      let updatedProduct = getInventoryFromVariations({
        channel,
        product,
        variations: operation.get(VALUE),
      });

      const overset = updatedProduct.getIn([FORM.INVENTORY, ATTRIBUTE.CPI, OVERSET]);

      if (overset) {
        updatedProduct = updatedProduct.setIn([FORM.VARIATIONS, ERROR],
          getCombinationsErrors({ channel, shopData, variations: updatedProduct.get(FORM.VARIATIONS) })
        );

        updatedProduct = updatedProduct
          .getIn([FORM.VARIATIONS, ERROR])
          .reduce(updateProductErrorsFromVariations, updatedProduct);
      } else {
        updatedProduct = setCPIErrors({ product: updatedProduct });
      }

      const error = overset
        ? updatedProduct.getIn([ERROR, FORM.VARIATIONS, ATTRIBUTE.CPI])
        : updatedProduct.getIn([ERROR, ATTRIBUTE.CPI]);

      if (!error) {
        product = updatedProduct;
      }

      break;
    }

    case OPERATIONS[SHOPIFY].CHARGE_TAX.INLINE_EDIT:
    case OPERATIONS[SHOPIFY].CONTINUE_SELLING.INLINE_EDIT:
    case OPERATIONS[SHOPIFY].COUNTRY_CODE.INLINE_EDIT:
    case OPERATIONS[SHOPIFY].HS_CODE.INLINE_EDIT: {
      return getInventoryFromVariations({
        channel,
        product,
        variations: operation.get(VALUE),
      });
    }

    case OPERATIONS[SHOPIFY].PHYSICAL.INLINE_EDIT:
    case OPERATIONS[SHOPIFY].WEIGHT.INLINE_EDIT: {
      let updatedProduct = getInventoryFromVariations({
        channel,
        product,
        variations: operation.get(VALUE),
      });

      const overset = updatedProduct.getIn([FORM.INVENTORY, ATTRIBUTE.PHYSICAL, OVERSET]);

      if (overset) {
        updatedProduct = updatedProduct.setIn([FORM.VARIATIONS, ERROR],
          getCombinationsErrors({ channel, shopData, variations: updatedProduct.get(FORM.VARIATIONS) })
        );

        updatedProduct = updatedProduct
          .getIn([FORM.VARIATIONS, ERROR])
          .reduce(updateProductErrorsFromVariations, updatedProduct);
      } else {
        updatedProduct = setWeightErrors({ product: updatedProduct });
      }

      const error = overset
        ? updatedProduct.getIn([ERROR, FORM.VARIATIONS, ATTRIBUTE.WEIGHT])
        : updatedProduct.getIn([ERROR, ATTRIBUTE.WEIGHT]);

      if (!error) {
        product = updatedProduct;
      }

      break;
    }

    case OPERATIONS[SHOPIFY].PRICE.INLINE_EDIT: {
      let updatedProduct = getInventoryFromVariations({
        channel,
        product,
        variations: operation.get(VALUE),
      });

      const overset = updatedProduct.getIn([FORM.INVENTORY, ATTRIBUTE.PRICE, OVERSET]);

      if (overset) {
        updatedProduct = updatedProduct.setIn([FORM.VARIATIONS, ERROR],
          getCombinationsErrors({ channel, shopData, variations: updatedProduct.get(FORM.VARIATIONS) })
        );

        updatedProduct = updatedProduct
          .getIn([FORM.VARIATIONS, ERROR])
          .reduce(updateProductErrorsFromVariations, updatedProduct);
      } else {
        updatedProduct = setPriceErrors({ product });
      }

      const error = overset
        ? updatedProduct.getIn([ERROR, FORM.VARIATIONS, ATTRIBUTE.PRICE])
        : updatedProduct.getIn([ERROR, ATTRIBUTE.PRICE]);

      if (!error) {
        product = updatedProduct;
      }

      break;
    }

    case OPERATIONS[SHOPIFY].QUANTITY.INLINE_EDIT: {
      let updatedProduct = getInventoryFromVariations({
        channel,
        product,
        variations: operation.get(VALUE),
      });

      const overset = updatedProduct.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, OVERSET]);

      if (overset) {
        updatedProduct = updatedProduct.setIn([FORM.VARIATIONS, ERROR],
          getCombinationsErrors({ channel, shopData, variations: updatedProduct.get(FORM.VARIATIONS) })
        );

        updatedProduct = updatedProduct
          .getIn([FORM.VARIATIONS, ERROR])
          .reduce(updateProductErrorsFromVariations, updatedProduct);
      } else {
        updatedProduct = setQuantityErrors({ product: updatedProduct });
      }

      const error = overset
        ? updatedProduct.getIn([ERROR, FORM.VARIATIONS, ATTRIBUTE.QUANTITY])
        : updatedProduct.getIn([ERROR, ATTRIBUTE.QUANTITY]);

      if (!error) {
        product = updatedProduct;
      }

      break;
    }

    case OPERATIONS[SHOPIFY].SKU.INLINE_EDIT: {
      let updatedProduct = getInventoryFromVariations({
        channel,
        product,
        variations: operation.get(VALUE),
      });

      const overset = updatedProduct.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, OVERSET]);

      if (overset) {
        updatedProduct = updatedProduct.setIn([FORM.VARIATIONS, ERROR],
          getCombinationsErrors({ channel, shopData, variations: updatedProduct.get(FORM.VARIATIONS) })
        );

        updatedProduct = updatedProduct
          .getIn([FORM.VARIATIONS, ERROR])
          .reduce(updateProductErrorsFromVariations, updatedProduct);
      } else {
        updatedProduct = setSKUErrors({ product: updatedProduct });
      }

      const error = overset
        ? updatedProduct.getIn([ERROR, FORM.VARIATIONS, ATTRIBUTE.SKU])
        : updatedProduct.getIn([ERROR, ATTRIBUTE.SKU]);

      if (!error) {
        product = updatedProduct;
      }

      break;
    }

    default: {
      break;
    }
  }

  return product;
}

export default function variations({ actions, operation, product, shop, shopData }) {
  switch (product.get('channel')) {
    case EBAY: {
      return { actions, product: updateEbayVariations({ operation, product, shop, shopData }) };
    }

    case ETSY: {
      return { actions, product: updateEtsyVariations({ operation, product, shop, shopData }) };
    }

    case FAIRE: {
      return { actions, product: updateFaireVariations({ operation, product, shop, shopData }) };
    }

    case SHOPIFY: {
      return { actions, product: updateShopifyVariations({ operation, product, shop, shopData }) };
    }

    default: {
      return { actions, product };
    }
  }
}
